<template>
  <div v-if="company" class="container pb-6">
    <div
      class="d-flex justify-content-center mb-5 mt-5 align-items-end company-name"
    >
      <h2 class="mx-2">{{ company.name }}</h2>
      <h3 class="fw-normal">({{ company.registry_code }})</h3>
      <label
        v-if="is_admin"
        class="alert alert-subtle-warning text-center admin-extra-info"
      >
        {{ company.id }}
      </label>
    </div>
    <div class="p-3 mb-4">
      <div class="row justify-content-between g-3">
        <div class="col-6 col-lg-4 text-center border-translucent border-end">
          <PendingPayoutFigure class="mb-3" :parts="moneyPendingParts" />
          <h4 class="mb-3 fs-8 font-serif">
            {{ $t("dashboard.companyData.money_pending") }}
          </h4>
          <button
            class="btn btn-phoenix-secondary"
            @click="setDialogState('sales-transactions')"
          >
            <font-awesome-icon class="px-1" icon="history" />
            {{ $t("dashboard.companyData.transaction_history") }}
          </button>
        </div>
        <div
          class="col-6 col-lg-4 text-center border-translucent border-end-lg"
        >
          <div
            class="d-inline-flex justify-content-center mb-3 align-items-end"
          >
            <h3 class="mx-2 mb-0 fs-6">{{ certificatesCount }}</h3>
            <div class="fs-9 text-muted">{{ $t("common.mwh") }}</div>
          </div>
          <h4 class="mb-3 fs-8 font-serif">
            {{ $t("dashboard.companyData.origin_certificates") }}
          </h4>
          <button
            class="btn btn-phoenix-secondary"
            :disabled="certificates.length == 0"
            @click="setDialogState('certificates-history')"
          >
            <font-awesome-icon class="px-1" icon="eye" />
            {{ $t("dashboard.companyData.view_details") }}
          </button>
        </div>
        <div class="col-12 col-lg-4 text-center d-grid">
          <button
            class="btn btn-subtle-primary mb-2 btn-lg"
            @click="setDialogState('request-invoice')"
            :disabled="pendingPayouts.length === 0"
          >
            <font-awesome-icon class="px-1" icon="file-invoice-dollar" />
            {{ $t("dashboard.companyData.submit_invoice") }}
          </button>
          <button
            class="btn mb-2 btn-phoenix-secondary"
            @click="setDialogState('invoice-history')"
          >
            <font-awesome-icon class="px-1" icon="folder-open" />
            {{ $t("dashboard.companyData.invoice_history") }}
          </button>
          <button
            class="btn btn-phoenix-secondary"
            @click="setDialogState('settings')"
          >
            <font-awesome-icon class="px-1" :icon="['fas', 'gear']" />
            {{ $t("settings.title") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Action Required Section / Thank You Section -->
    <FollowUpSection :company="company" :account-id="account_id" class="mb-3" />

    <Transition name="expand">
      <!-- Special card for dialog components-->
      <div id="dialog-card" class="card p-3 mb-3" v-if="!!dialogState">
        <button
          id="dialog-close-button"
          class="btn p-1"
          type="button"
          aria-label="Close"
          @click="closeDialog()"
        >
          <font-awesome-icon icon="times" />
        </button>
        <div class="card-body p-0">
          <div class="mt-2">
            <request-invoice-component
              v-if="dialogState === 'request-invoice'"
              :company="company"
              :pendingInvoiceItems="pendingInvoiceItems"
              :defaultInvoiceNumber="defaultInvoiceNumber"
              :monthSplit="monthSplit"
              :pendingPayoutByCompanyId="pendingPayoutByCompanyId"
              @invoice-requested="handleInvoiceRequested"
            />
            <CertificateHistoryComponent
              v-if="dialogState === 'certificates-history'"
              :certificates="certificates"
            />
            <InvoiceHistoryComponent
              v-if="dialogState === 'invoice-history' && company.id"
              :company_id="company.id"
              :companies="[company]"
              :is_admin="is_admin"
            />
            <SalesTransactionsComponent
              v-if="dialogState === 'sales-transactions' && company.id"
              :company-ids="[company.id]"
              :companies="[company]"
              :nda_signed_link="company.nda_signed_link"
            />
            <SettingsComponent
              v-if="dialogState === 'settings' && company.id"
              :companyId="company.id"
              :company="company"
            />
          </div>
        </div>
      </div>
    </Transition>

    <!-- Everything below here is only shown if the company has signed the Soldera contract -->
    <div v-if="hasSignedSolderaContract">
      <TradingStrategyManagementComponent
        :company="company"
        :is_admin="is_admin"
      />

      <ReferralLinkCard
        v-if="company.id && showReferral"
        :company-id="company.id"
      />

      <!-- Production Devices Section -->
      <RegistryDevicesSection
        v-if="
          registryDevicesStore.registryDevices &&
          registryDevicesStore.registryDevices.length > 0
        "
        :company="company"
        bordered
      />

      <!-- Documents Section -->
      <DocumentsSection
        v-if="company.documents && company.documents.length > 0"
        :documents="company.documents"
        :generators="company.generators"
        :account_id="account_id"
        :is_admin="is_admin"
        :showPlaceholder="false"
      />
    </div>
  </div>
  <loading-overlay-component v-if="isLoading"></loading-overlay-component>
</template>

<script setup lang="ts">
import {
  pendingPayoutFigureParts,
  pendingPayoutsByPayoutCurrency,
} from "@/composables/money/invoiceUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useRegistryDevicesStore } from "@/composables/useRegistryDevicesStore";
import FollowUpSection from "@/dashboard/common/FollowUpSection.vue";
import ReferralLinkCard from "@/dashboard/common/ReferralLinkCard.vue";
import DocumentsSection from "@/dashboard/invoice_management/DocumentsSection.vue";
import TradingStrategyManagementComponent from "@/dashboard/production_strategy/TradingStrategyManagementComponent.vue";
import SettingsComponent from "@/dashboard/settings_management/SettingsComponent.vue";
import CertificateHistoryComponent from "@/dashboard/small_producer/CertificateHistoryComponent.vue";
import InvoiceHistoryComponent from "@/money/invoice/InvoiceHistoryComponent.vue";
import PendingPayoutFigure from "@/money/PendingPayoutFigure.vue";
import RequestInvoiceComponent from "@/money/invoice/RequestInvoiceComponent.vue";
import SalesTransactionsComponent from "@/money/SalesTransactionsComponent.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, onMounted, ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import LoadingOverlayComponent from "../../components/common/LoadingOverlayComponent.vue";
import type {
  Certificate,
  Company,
  Document,
  InvoiceItem,
  MonthSplit,
  PendingPayoutByCompanyId,
} from "../../types";
import RegistryDevicesSection from "../production_strategy/RegistryDevicesSection.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = defineProps({
  company: {
    type: Object as PropType<Company>,
    required: true,
  },
  account_id: {
    type: Number,
    required: true,
  },
  is_admin: {
    type: Boolean,
    required: true,
  },
  showReferral: {
    type: Boolean,
    required: true,
  },
});

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler: handleSuccess } =
  useGlobalHandlers();
const dialogState = ref("");
const isLoading = ref(false);
const registryDevicesStore = useRegistryDevicesStore();
const certificates = ref<[Certificate] | []>([]);
const certificatesCount = ref(0);
const pendingInvoiceItems = ref<InvoiceItem[]>([]);
const defaultInvoiceNumber = ref("");
const monthSplit = ref<MonthSplit | null>(null);
const pendingPayoutByCompanyId = ref<PendingPayoutByCompanyId>({});
const pendingPayouts = computed(() =>
  pendingPayoutsByPayoutCurrency(
    pendingInvoiceItems.value,
    [props.company],
    pendingPayoutByCompanyId.value
  )
);
// Nothing fetched reads "-"; a balance nothing is owed on shows as it is.
const moneyPendingParts = computed(() =>
  pendingInvoiceItems.value.length === 0
    ? []
    : pendingPayoutFigureParts(
        pendingInvoiceItems.value,
        props.company,
        pendingPayoutByCompanyId.value
      )
);

const setDialogState = (state: string) => {
  dialogState.value = state;
};

const closeDialog = () => {
  dialogState.value = "";
};

const hasSignedSolderaContract = computed(() => {
  return (
    props.company.has_registered ||
    props.company.documents.some((doc: Document) => doc.status === "completed")
  );
});

async function handleInvoiceRequested() {
  handleSuccess(
    t("dashboard.companyData.invoice_submission_success"),
    t("dashboard.companyData.invoice_paid_within_7_days")
  );
  pendingInvoiceItems.value = [];
  monthSplit.value = null;
  pendingPayoutByCompanyId.value = {};
  closeDialog();
}

async function getPendingInvoiceItems() {
  try {
    const response = await goSolidApi.get(
      `/api/company/${props.company.id}/pending-invoice-items`
    );
    if (response.status === 200) {
      pendingInvoiceItems.value = response.data.invoice_items;
      defaultInvoiceNumber.value = response.data.default_invoice_number;
      monthSplit.value = response.data.month_split;
      pendingPayoutByCompanyId.value =
        props.company.id === undefined
          ? {}
          : { [props.company.id]: response.data.pending_payout };
    } else {
      throw new Error(
        t("dashboard.companyData.fetch_pending_invoice_items_failed")
      );
    }
  } catch (error) {
    globalErrorHandler(error);
  }
}

async function fetchCertificates() {
  try {
    const response = await goSolidApi.get(
      `/api/company/certificates/${props.company.id}`
    );
    if (response.status === 200) {
      certificates.value = response.data.certificates;
      certificatesCount.value = response.data.count;
    } else {
      throw new Error(t("dashboard.companyData.fetch_certificates_failed"));
    }
  } catch (error) {
    globalErrorHandler(error);
  }
}

onMounted(async () => {
  isLoading.value = true;
  await Promise.all([
    fetchCertificates(),
    registryDevicesStore.fetchRegistryDevices(props.company.id),
    getPendingInvoiceItems(),
  ]);
  isLoading.value = false;
});
</script>

<style scoped lang="scss">
.container {
  text-align: left;
  max-width: 800px;
}
.admin-extra-info {
  padding: 0;
}

#dialog-close-button {
  position: absolute;
  top: 10px;
  right: 15px;
  z-index: 1000;
  background-color: transparent;
  border: none;
  font-size: 1.3rem;
  color: var(--bs-dark);
}

/* The close button floats over the card's top-right corner; move the invoice
   history export button to the left and pull it up so it lines up with the X */
#dialog-card :deep(.export-toolbar) {
  /* stylelint-disable-next-line declaration-no-important -- outweighs the Bootstrap justify-content-end utility */
  justify-content: flex-start !important;
  margin-top: -14px;
}

@media (max-width: 768px) {
  .company-name {
    display: none !important;
  }
}

/* Transition Styles */
.expand-enter-active,
.expand-leave-active {
  transition:
    max-height 0.4s ease-in-out,
    opacity 0.5s ease; /* Adjust timing to suit */
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0; /* Start from 0 height */
  opacity: 0;
  overflow: hidden;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 500px; /* Adjust to the maximum height you expect your card to have */
  opacity: 1;
  overflow: auto;
}
</style>
